import toast from 'react-hot-toast';
import { takeEvery, put, call, select } from 'redux-saga/effects';
import * as showcaseApi from '../../api/showcaseApi';
import * as actions from '../actions';
import * as selectors from '../selectors';
import { waitFor } from './sagaUtils';

export function* getListings() {
  try {
    yield put(actions.user.setLoading(true));

    const filter = {};
    const query = yield select(selectors.listings.query);
    yield call(waitFor, state => state.firm.firmsForLookup);
    const firms = yield select(selectors.firms.firmsForUserCompany);
    const isGowUser = yield select(selectors.users.isGowUser);

    if (!isGowUser) {
      filter.showcaseFirmId = { in: firms };
    } else if (query?.showcaseFirmId) {
      filter.showcaseFirmId = { eq: query.showcaseFirmId };
    }

    if (query?.description1) {
      filter.description1 = { iLike: `%${query.description1}%` };
    }

    if (query?.description2) {
      filter.description2 = { iLike: `%${query.description2}%` };
    }

    if (query?.line1) {
      filter.line1 = { iLike: `%${query.line1}%` };
    }

    if (query?.line2) {
      filter.line2 = { iLike: `%${query.line2}%` };
    }

    if (query?.address) {
      filter.address = { iLike: `%${query.address}%` };
    }

    if (query?.type) {
      filter.type = { iLike: `%${query.type}%` };
    }

    if (query?.name) {
      filter.name = { iLike: `%${query.name}%` };
    }

    yield put(actions.user.setLoading(true));
    const page = yield select(selectors.listings.activePage);
    const pageSize = 20;

    let { listings, count } = yield call(showcaseApi.getFirmItems, {
      filter,
      page,
      pageSize,
      offset: (page - 1) * pageSize,
    });

    yield put(actions.user.setLoading(true));
    const url = imageId => `https://res.cloudinary.com/culturemap-com/image/upload/c_scale,w_500/photos/${imageId}`;
    const isCloudinary = listing => listing.imageUrl?.includes('cloudinary');
    const imageId = listing => listing.imageUrl?.split('/').pop();
    const imageUrl = listing => (isCloudinary(listing) ? url(imageId(listing)) : listing.imageUrl);

    listings = listings
      .map(listing => ({
        ...listing,
        imageUrl: imageUrl(listing),
      }))
      .filter(a => !a.isDeleted);

    yield put(actions.listing.getListingsSuccess(listings, count));
  } catch (e) {
    yield put(actions.listing.getListingsFail(e.message));
    toast.error('An error occurred retrieving listings.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* getListingById({ payload: { id } }) {
  try {
    yield put(actions.user.setLoading(true));
    const listing = yield call(showcaseApi.getFirmItemById, id);
    yield put(actions.listing.getListingByIdSuccess(listing));
  } catch (e) {
    yield put(actions.listing.getListingByIdFail(e.message));
    toast.error('An error occurred retrieving listing.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* addListing({ payload: { listing } }) {
  try {
    yield put(actions.user.setLoading(true));
    const _listing = yield call(showcaseApi.addFirmItem, listing);
    yield put(actions.listing.addListingSuccess(_listing));
    yield put(actions.firm.getFirmById(listing.showcaseFirmId));
    yield put(actions.listing.getListings());
  } catch (e) {
    yield put(actions.listing.addListingFail(e.message));
    toast.error('An error occurred updating listing.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* updateListing({ payload: { listing } }) {
  try {
    yield put(actions.user.setLoading(true));
    const _listing = yield call(showcaseApi.updateFirmItem, listing);
    yield put(actions.listing.updateListingSuccess(_listing));

    const { id } = yield select(selectors.showcase.currentAdSlot);
    yield put(actions.adSlot.getAdSlotById(id));
    yield put(actions.firm.getFirmById(listing.showcaseFirmId));
    yield put(actions.listing.getListings());
  } catch (e) {
    yield put(actions.listing.updateListingFail(e.message));
    toast.error('An error occurred updating listing.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* searchListings({ payload: { query } }) {
  try {
    yield put(actions.user.setLoading(true));
    const listings = yield call(showcaseApi.searchListings, query);
    yield put(actions.listing.searchListingsSuccess(listings));
  } catch (e) {
    yield put(actions.listing.searchListingsFail(e.message));
    toast.error('An error occurred retrieving the listings.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* paginate({ payload: { activePage } }) {
  yield put(actions.listing.getListings());
}

export function* listingSagas() {
  yield takeEvery(actions.listing.getListings, getListings);
  yield takeEvery(actions.listing.getListingById, getListingById);
  yield takeEvery(actions.listing.updateListing, updateListing);
  yield takeEvery(actions.listing.addListing, addListing);
  yield takeEvery(actions.listing.searchListings, searchListings);
  yield takeEvery(actions.listing.paginate, paginate);
}
